import React, { useState, useEffect } from 'react';
import { 
  Search, 
  Filter, 
  Globe, 
  Calendar, 
  ExternalLink, 
  BookOpen, 
  GraduationCap,
  Award,
  Briefcase,
  MapPin,
  Clock,
  ChevronDown,
  ChevronUp,
  X
} from 'lucide-react';
import { apiRequest } from '../utils/api';
import { useUsage } from '../contexts/UsageContext';
import { useAuth } from '../components/auth/AuthComponents';
import { useSubscription } from '../hooks/useSubscription';
import UnauthorizedBox from '../components/shared/UnauthorizedBox';
import SubscriptionNotification from '../components/shared/SubscriptionNotification';

const SOPOpportunitiesPage = () => {
  const { user, isAdmin, isAuthenticated } = useAuth();
  const { loading: subLoading } = useSubscription();
  const { usage, subscription, refreshUsage } = useUsage();
  const [opportunities, setOpportunities] = useState([]);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    country: '',
    opportunityType: '',
    programType: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'matches', 'scholarships', 'visas', 'study'
  const [stats, setStats] = useState({});
  const [sources, setSources] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [perPage] = useState(50);
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const hasAccess = isAdmin || isAuthenticated;


  useEffect(() => {
    if (hasAccess) {
      fetchOpportunities();
      fetchMatches();
      fetchStats();
      fetchSources();
    }
  }, [hasAccess, currentPage, filters, activeTab]);

  const fetchOpportunities = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      if (filters.country) params.append('country', filters.country);
      if (filters.opportunityType) params.append('opportunity_type', filters.opportunityType);
      if (filters.programType) params.append('program_type', filters.programType);
      
      // Add opportunity type filter based on active tab
      if (activeTab === 'scholarships') {
        params.append('opportunity_type', 'scholarship');
      } else if (activeTab === 'visas') {
        params.append('opportunity_type', 'work_visa');
      } else if (activeTab === 'study') {
        params.append('opportunity_type', 'study_abroad');
      }
      // For 'all' tab, don't add any opportunity_type filter
      
      // Add pagination parameters
      params.append('limit', String(perPage));
      params.append('offset', String((currentPage - 1) * perPage));
      
      const data = await apiRequest(`sop-opportunities/opportunities?${params}`);
      setOpportunities(data.opportunities || []);
      setTotalCount(data.total_count || 0);
    } catch (error) {
      console.error('Error fetching opportunities:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMatches = async () => {
    try {
      const data = await apiRequest('sop-opportunities/matches');
      setMatches(data.matches || []);
    } catch (error) {
      console.error('Error fetching matches:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const data = await apiRequest('sop-opportunities/stats');
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchSources = async () => {
    try {
      const data = await apiRequest('sop-opportunities/sources');
      setSources(data.sources || []);
    } catch (error) {
      console.error('Error fetching sources:', error);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    setCurrentPage(1);
    setShowFilters(false);
  };

  const clearFilters = () => {
    setFilters({ country: '', opportunityType: '', programType: '' });
    setSearchTerm('');
    setCurrentPage(1);
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setCurrentPage(1);
  };

  const handleViewOpportunity = (opportunity) => {
    setSelectedOpportunity(opportunity);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedOpportunity(null);
  };

  const markAsViewed = async (matchId) => {
    try {
      await apiRequest(`sop-opportunities/matches/${matchId}/view`, {
        method: 'POST'
      });
      // Refresh matches to update viewed status
      fetchMatches();
    } catch (error) {
      console.error('Error marking as viewed:', error);
    }
  };

  const markAsApplied = async (matchId) => {
    try {
      await apiRequest(`sop-opportunities/matches/${matchId}/applied`, {
        method: 'POST'
      });
      // Refresh matches to update applied status
      fetchMatches();
    } catch (error) {
      console.error('Error marking as applied:', error);
    }
  };

  const getOpportunityIcon = (type) => {
    switch (type) {
      case 'scholarship':
        return <Award className="w-5 h-5 text-yellow-400" />;
      case 'work_visa':
        return <Briefcase className="w-5 h-5 text-blue-400" />;
      case 'study_abroad':
        return <GraduationCap className="w-5 h-5 text-green-400" />;
      default:
        return <BookOpen className="w-5 h-5 text-[var(--color-text-muted)]" />;
    }
  };

  const getOpportunityTypeColor = (type) => {
    switch (type) {
      case 'scholarship':
        return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'work_visa':
        return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'study_abroad':
        return 'bg-green-500/20 text-green-400 border-green-500/30';
      default:
        return 'bg-gray-500/20 text-[var(--color-text-muted)] border-gray-500/30';
    }
  };

  // Get the data to display based on active tab
  const getDisplayData = () => {
    if (activeTab === 'matches') {
      return matches.filter(match => {
        const matchesSearch = match.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                             match.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                             match.country?.toLowerCase().includes(searchTerm.toLowerCase());
        return matchesSearch;
      });
    } else {
      // For other tabs, opportunities are already filtered by the API
      return opportunities.filter(opp => {
        const matchesSearch = opp.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                             opp.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                             opp.country?.toLowerCase().includes(searchTerm.toLowerCase());
        return matchesSearch;
      });
    }
  };

  const filteredOpportunities = getDisplayData();

  const OpportunityCard = ({ opportunity, isMatch = false, matchData = null }) => (
    <div className="elevated-card backdrop-blur-sm border border-[var(--color-border)] rounded-2xl p-5 hover:bg-[var(--color-surface-muted)] transition-all">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-start space-x-3 flex-1 min-w-0">
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-[var(--color-text)] leading-tight flex items-center">
              {getOpportunityIcon(opportunity.opportunity_type)}
              <span className="ml-2 truncate" style={{ maxWidth: '25ch' }}>
                {opportunity.title}
              </span>
            </h3>
            {opportunity.institution && (
              <p className="text-sm text-[var(--color-text-secondary)] mt-1 line-clamp-1">{opportunity.institution}</p>
            )}
          </div>
        </div>
        <div className="flex-shrink-0 ml-2">
          <span className={`px-2 py-1 text-xs font-medium rounded-full border whitespace-nowrap ${getOpportunityTypeColor(opportunity.opportunity_type)}`}>
            {opportunity.opportunity_type.replace('_', ' ').toUpperCase()}
          </span>
        </div>
      </div>

      <div className="space-y-1.5 mb-3">
        {opportunity.country && (
          <div className="flex items-center text-sm text-[var(--color-text-secondary)]">
            <MapPin className="w-4 h-4 mr-2 flex-shrink-0" />
            <span className="truncate">{opportunity.country}</span>
          </div>
        )}
        {opportunity.program_type && (
          <div className="flex items-center text-sm text-[var(--color-text-secondary)]">
            <GraduationCap className="w-4 h-4 mr-2 flex-shrink-0" />
            <span className="truncate">{opportunity.program_type}</span>
          </div>
        )}
        {opportunity.field_of_study && (
          <div className="flex items-center text-sm text-[var(--color-text-secondary)]">
            <BookOpen className="w-4 h-4 mr-2 flex-shrink-0" />
            <span className="truncate">{opportunity.field_of_study}</span>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center text-xs text-[var(--color-text-muted)]">
          <Globe className="w-3 h-3 mr-1" />
          Source: {opportunity.source}
        </div>
        
        <div className="flex items-center space-x-2">
          
          {opportunity.application_url && (
            <a
              onClick={() => handleViewOpportunity(opportunity)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-3 py-1 text-xs font-medium text-[var(--color-text)] bg-[var(--color-primary)] rounded-md hover:bg-[var(--color-primary-hover)]"
            >
              View
            </a>
          )}
        </div>
      </div>
    </div>
  );

  if (!isAuthenticated && !subLoading) {
    return (
      <UnauthorizedBox
        title="Sign in required"
        message="Sign in to browse SOP opportunities and view your AI matches."
        upgradeMessage=""
        featureName="SOP Opportunities"
      />
    );
  }

  if (subLoading) {
    return <div className="min-h-screen flex items-center justify-center text-[var(--color-text-secondary)]">Loading...</div>;
  }

  if (!hasAccess) {
    return (
      <UnauthorizedBox
        title="SOP Opportunities Access Required"
        message="Browse study, visa, and scholarship opportunities. Upgrade for more SOP matches per run."
        upgradeMessage="Upgrade your plan for more SOP matches per run."
        featureName="SOP Opportunities"
      />
    );
  }

  return (
    <div className="min-h-screen bg-[var(--color-bg)]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-[var(--color-text)] mb-2">
            Study & Visa <span className="bg-gradient-to-r from-[var(--color-gradient-from)] to-[var(--color-gradient-to)] bg-clip-text text-transparent">Opportunities</span>
          </h1>
          <p className="text-[var(--color-text-secondary)]">
            Discover study abroad programs, work visas, and scholarship opportunities matched to your profile.
          </p>
        </div>

        {/* Subscription Notification */}
        <SubscriptionNotification />

        {/* Stats Cards */}
        {stats.total_opportunities && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8 mt-6">
            <div className="elevated-card backdrop-blur-sm border border-[var(--color-border)] rounded-2xl p-6">
              <div className="flex items-center">
                <div className="p-2 bg-blue-500/20 rounded-lg">
                  <BookOpen className="w-6 h-6 text-blue-400" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-[var(--color-text-muted)]">Total Opportunities</p>
                  <p className="text-2xl font-bold text-[var(--color-text)]">{stats.total_opportunities}</p>
                </div>
              </div>
            </div>
            
            <div className="elevated-card backdrop-blur-sm border border-[var(--color-border)] rounded-2xl p-6">
              <div className="flex items-center">
                <div className="p-2 bg-green-500/20 rounded-lg">
                  <Calendar className="w-6 h-6 text-green-400" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-[var(--color-text-muted)]">Recent (7 days)</p>
                  <p className="text-2xl font-bold text-[var(--color-text)]">{stats.recent_opportunities}</p>
                </div>
              </div>
            </div>
            
            <div className="elevated-card backdrop-blur-sm border border-[var(--color-border)] rounded-2xl p-6">
              <div className="flex items-center">
                <div className="p-2 bg-yellow-500/20 rounded-lg">
                  <Award className="w-6 h-6 text-yellow-400" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-[var(--color-text-muted)]">Your Matches</p>
                  <p className="text-2xl font-bold text-[var(--color-text)]">{matches.length}</p>
                </div>
              </div>
            </div>
            
            <div className="elevated-card backdrop-blur-sm border border-[var(--color-border)] rounded-2xl p-6">
              <div className="flex items-center">
                <div className="p-2 bg-[var(--color-primary-soft)] rounded-lg">
                  <Globe className="w-6 h-6 text-[var(--color-primary)]" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-[var(--color-text-muted)]">Countries</p>
                  <p className="text-2xl font-bold text-[var(--color-text)]">{stats.top_countries?.length || 0}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Search and Filters */}
        <div className="elevated-card backdrop-blur-sm border border-[var(--color-border)] rounded-2xl p-6 mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
            <div className="flex-1 max-w-lg">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[var(--color-text-muted)] w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search opportunities..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-[var(--color-surface-muted)] border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-ring)] focus:border-transparent text-[var(--color-text)] placeholder-[var(--color-placeholder)]"
                />
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center px-4 py-2 text-sm font-medium text-[var(--color-text-secondary)] bg-[var(--color-surface-muted)] border border-[var(--color-border)] rounded-lg hover:bg-[var(--color-surface-muted)]"
              >
                <Filter className="w-4 h-4 mr-2" />
                Filters
                {showFilters ? <ChevronUp className="w-4 h-4 ml-2" /> : <ChevronDown className="w-4 h-4 ml-2" />}
              </button>
            </div>
          </div>

          {/* Filter Panel */}
          {showFilters && (
            <div className="mt-6 pt-6 border-t border-[var(--color-border)]">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2">Country</label>
                  <select
                    value={filters.country}
                    onChange={(e) => handleFilterChange('country', e.target.value)}
                    className="w-full px-3 py-2 bg-[var(--color-surface-muted)] border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-ring)] focus:border-transparent text-[var(--color-text)]"
                  >
                    <option value="">All Countries</option>
                    {stats.top_countries?.map(country => (
                      <option key={country.country} value={country.country}>
                        {country.country} ({country.count})
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2">Type</label>
                  <select
                    value={filters.opportunityType}
                    onChange={(e) => handleFilterChange('opportunityType', e.target.value)}
                    className="w-full px-3 py-2 bg-[var(--color-surface-muted)] border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-ring)] focus:border-transparent text-[var(--color-text)]"
                  >
                    <option value="">All Types</option>
                    <option value="scholarship">Scholarships</option>
                    <option value="work_visa">Work Visas</option>
                    <option value="study_abroad">Study Abroad</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2">Program Type</label>
                  <select
                    value={filters.programType}
                    onChange={(e) => handleFilterChange('programType', e.target.value)}
                    className="w-full px-3 py-2 bg-[var(--color-surface-muted)] border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-ring)] focus:border-transparent text-[var(--color-text)]"
                  >
                    <option value="">All Programs</option>
                    <option value="Masters">Masters</option>
                    <option value="PhD">PhD</option>
                    <option value="Certificate">Certificate</option>
                    <option value="Diploma">Diploma</option>
                  </select>
                </div>
              </div>
              
              <div className="flex items-center justify-end space-x-4 mt-4">
                <button
                  onClick={clearFilters}
                  className="px-4 py-2 text-sm font-medium text-[var(--color-text-secondary)] bg-[var(--color-surface-muted)] border border-[var(--color-border)] rounded-lg hover:bg-[var(--color-surface-muted)]"
                >
                  Clear Filters
                </button>
                <button
                  onClick={applyFilters}
                  className="px-4 py-2 text-sm font-medium text-[var(--color-text)] bg-[var(--color-primary)] rounded-lg hover:bg-[var(--color-primary-hover)]"
                >
                  Apply Filters
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="flex space-x-1 mb-6">
          {[
            { id: 'all', label: 'All Opportunities', count: activeTab === 'all' ? totalCount : (stats.total_opportunities || 0) },
            { id: 'matches', label: 'Your Matches', count: matches.length },
            { id: 'scholarships', label: 'Scholarships', count: activeTab === 'scholarships' ? totalCount : (stats.opportunity_types?.find(t => t.type === 'scholarship')?.count || 0) },
            { id: 'visas', label: 'Work Visas', count: activeTab === 'visas' ? totalCount : (stats.opportunity_types?.find(t => t.type === 'work_visa')?.count || 0) },
            { id: 'study', label: 'Study Abroad', count: activeTab === 'study' ? totalCount : (stats.opportunity_types?.find(t => t.type === 'study_abroad')?.count || 0) },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                activeTab === tab.id
                  ? 'bg-[var(--color-primary)] text-[var(--color-text-inverse)] border border-[var(--color-primary)]'
                  : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:bg-[var(--color-surface-muted)]'
              }`}
            >
              {tab.label} ({tab.count})
            </button>
          ))}
        </div>

        {/* Opportunities Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--color-primary)]"></div>
            <span className="ml-3 text-[var(--color-text-secondary)]">Loading opportunities...</span>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {filteredOpportunities.map(opportunity => {
                // For matches tab, the opportunity is already a match object
                const isMatch = activeTab === 'matches';
                const matchData = isMatch ? opportunity : matches.find(match => match.crawl_sop_id === opportunity.id);
                return (
                  <OpportunityCard
                    key={opportunity.id}
                    opportunity={opportunity}
                    isMatch={isMatch}
                    matchData={matchData}
                  />
                );
              })}
            </div>

            {/* Pagination - only show for non-matches tabs */}
            {activeTab !== 'matches' && totalCount > perPage && (
              <div className="flex items-center justify-between mt-8">
                <div className="text-[var(--color-text-muted)] text-sm">
                  Showing {((currentPage - 1) * perPage) + 1} to {Math.min(currentPage * perPage, totalCount)} of {totalCount} opportunities
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-2 text-sm font-medium text-[var(--color-text-secondary)] bg-[var(--color-surface-muted)] rounded-lg hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <span className="text-[var(--color-text-secondary)] text-sm">
                    Page {currentPage} of {Math.ceil(totalCount / perPage)}
                  </span>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(Math.ceil(totalCount / perPage), prev + 1))}
                    disabled={currentPage >= Math.ceil(totalCount / perPage)}
                    className="px-3 py-2 text-sm font-medium text-[var(--color-text-secondary)] bg-[var(--color-surface-muted)] rounded-lg hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {filteredOpportunities.length === 0 && !loading && (
          <div className="text-center py-12">
            <BookOpen className="w-12 h-12 text-[var(--color-text-muted)] mx-auto mb-4" />
            <h3 className="text-lg font-medium text-[var(--color-text)] mb-2">No opportunities found</h3>
            <p className="text-[var(--color-text-muted)]">Try adjusting your search terms or filters.</p>
          </div>
        )}
      </div>

      {/* Opportunity Details Modal */}
      {showModal && selectedOpportunity && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80 backdrop-blur-sm">
          <div className="bg-[var(--color-surface)] rounded-2xl shadow-2xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              {/* Modal Header */}
              <div className="flex items-start justify-between mb-6">
                <div className="flex items-start space-x-4 flex-1">
                  <div className="flex-1 min-w-0">
                    <h2 className="text-2xl font-bold text-[var(--color-text)] mb-2 flex items-center">
                      {getOpportunityIcon(selectedOpportunity.opportunity_type)}
                      <span className="ml-3">{selectedOpportunity.title}</span>
                    </h2>
                    {selectedOpportunity.institution && (
                      <p className="text-lg text-[var(--color-text-secondary)]">{selectedOpportunity.institution}</p>
                    )}
                  </div>
                </div>
                <button
                  onClick={handleCloseModal}
                  className="text-[var(--color-text-muted)] hover:text-[var(--color-text)] transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Opportunity Type Badge */}
              <div className="mb-6">
                <span className={`px-3 py-1 text-sm font-medium rounded-full border ${getOpportunityTypeColor(selectedOpportunity.opportunity_type)}`}>
                  {selectedOpportunity.opportunity_type.replace('_', ' ').toUpperCase()}
                </span>
              </div>

              {/* Details Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                {selectedOpportunity.country && (
                  <div className="flex items-center text-[var(--color-text-secondary)]">
                    <MapPin className="w-5 h-5 mr-3 text-[var(--color-text-muted)]" />
                    <span className="font-medium">Country:</span>
                    <span className="ml-2">{selectedOpportunity.country}</span>
                  </div>
                )}
                {selectedOpportunity.program_type && (
                  <div className="flex items-center text-[var(--color-text-secondary)]">
                    <GraduationCap className="w-5 h-5 mr-3 text-[var(--color-text-muted)]" />
                    <span className="font-medium">Program Type:</span>
                    <span className="ml-2">{selectedOpportunity.program_type}</span>
                  </div>
                )}
                {selectedOpportunity.field_of_study && (
                  <div className="flex items-center text-[var(--color-text-secondary)]">
                    <BookOpen className="w-5 h-5 mr-3 text-[var(--color-text-muted)]" />
                    <span className="font-medium">Field of Study:</span>
                    <span className="ml-2">{selectedOpportunity.field_of_study}</span>
                  </div>
                )}
                {selectedOpportunity.deadline && (
                  <div className="flex items-center text-[var(--color-text-secondary)]">
                    <Clock className="w-5 h-5 mr-3 text-[var(--color-text-muted)]" />
                    <span className="font-medium">Deadline:</span>
                    <span className="ml-2">{new Date(selectedOpportunity.deadline).toLocaleDateString()}</span>
                  </div>
                )}
                {selectedOpportunity.source && (
                  <div className="flex items-start text-[var(--color-text-secondary)]">
                    <Globe className="w-5 h-5 mr-3 text-[var(--color-text-muted)] flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <span className="font-medium">Source:</span>
                      <span className="ml-2 break-all text-sm">{selectedOpportunity.source}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Description */}
              {selectedOpportunity.description && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-[var(--color-text)] mb-3">Description</h3>
                  <p className="text-[var(--color-text-secondary)] leading-relaxed">
                    {selectedOpportunity.description}
                  </p>
                </div>
              )}

              {/* Requirements */}
              {selectedOpportunity.requirements && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-[var(--color-text)] mb-3">Requirements</h3>
                  <p className="text-[var(--color-text-secondary)] leading-relaxed">
                    {selectedOpportunity.requirements}
                  </p>
                </div>
              )}

              {/* Key Benefits */}
              {selectedOpportunity.key_benefits && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-[var(--color-text)] mb-3">Key Benefits</h3>
                  <p className="text-[var(--color-text-secondary)] leading-relaxed">
                    {selectedOpportunity.key_benefits}
                  </p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex items-center justify-end space-x-4 pt-6 border-t border-[var(--color-border)]">
                <button
                  onClick={handleCloseModal}
                  className="px-4 py-2 text-sm font-medium text-[var(--color-text-secondary)] bg-[var(--color-surface-muted)] rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Close
                </button>
                {selectedOpportunity.application_url && (
                  <a
                    href={selectedOpportunity.application_url}
                    className="inline-flex items-center px-6 py-2 text-sm font-medium text-[var(--color-text)] bg-[var(--color-primary)] rounded-lg hover:bg-[var(--color-primary-hover)] transition-colors"
                  >
                    Apply Now
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SOPOpportunitiesPage;
